if (!customElements.get('storefront-facets')) {
  customElements.define('storefront-facets', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.drawer = this.querySelector('[data-facets-drawer]');
      this.drawerPanel = this.querySelector('[data-facets-drawer-panel]');
      this.sectionId = this.dataset.sectionId;
      this.resultsSection = document.querySelector('[data-collection-results-section]');

      const { signal } = this.abortController;
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.addEventListener('change', event => this.onChange(event), { signal });
      this.addEventListener('input', event => this.onInput(event), { signal });
      this.addEventListener('submit', event => this.onSubmit(event), { signal });
      document.addEventListener('click', event => this.onExternalClick(event), { signal });
      document.addEventListener('collection:items-appended', () => {
        this.resultsSection = document.querySelector('[data-collection-results-section]');
        this.applyLocalFilters();
      }, { signal });
      this.drawer?.addEventListener('cancel', event => {
        event.preventDefault();
        this.closeDrawer();
      }, { signal });
      this.drawer?.addEventListener('close', () => this.unlockPage(), { signal });
      window.addEventListener('popstate', () => this.render(location.href, { updateHistory: false }), { signal });
      this.initializePriceRanges();
      this.initializeLocalFilters();
      this.initializeStickyToolbar();
    }

    initializeLocalFilters() {
      this.localFilterParameter = 'local_filter';
      this.syncLocalFilterButtons();
      this.applyLocalFilters();
    }

    localFilterEntries() {
      return new URL(location.href).searchParams.getAll(this.localFilterParameter)
        .map(entry => {
          const separator = entry.indexOf(':');
          if (separator < 1) return null;
          return { key: entry.slice(0, separator), value: entry.slice(separator + 1) };
        })
        .filter(Boolean);
    }

    syncLocalFilterButtons() {
      const activeEntries = this.localFilterEntries();
      this.querySelectorAll('[data-source-type="local-filter"]').forEach(button => {
        const active = activeEntries.some(entry => (
          entry.key === button.dataset.localFilterKey && entry.value === button.dataset.localFilterValue
        ));
        button.dataset.active = String(active);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    toggleLocalFilter(button) {
      const key = button.dataset.localFilterKey;
      const value = button.dataset.localFilterValue;
      if (!key || !value) return;

      const url = new URL(location.href);
      const entry = `${key}:${value}`;
      const activeValues = url.searchParams.getAll(this.localFilterParameter);
      const wasActive = activeValues.includes(entry);
      const navigation = button.closest('[data-visual-navigation]');

      if (navigation?.dataset.selectionMode === 'single') {
        url.searchParams.delete(this.localFilterParameter);
      } else {
        url.searchParams.delete(this.localFilterParameter);
        activeValues.filter(current => current !== entry).forEach(current => {
          url.searchParams.append(this.localFilterParameter, current);
        });
      }

      if (!wasActive) url.searchParams.append(this.localFilterParameter, entry);
      url.searchParams.delete('page');
      history.pushState({}, '', url);
      this.syncLocalFilterButtons();
      this.applyLocalFilters();
    }

    applyLocalFilters() {
      const cards = [...(this.resultsSection?.querySelectorAll('[data-product-card]') || [])];
      const entries = this.localFilterEntries();
      let visibleCount = 0;

      cards.forEach(card => {
        const visible = entries.every(entry => card.getAttribute(`data-local-${entry.key}`)?.split('|').includes(entry.value));
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      this.resultsSection?.querySelectorAll('.collection-promo-card').forEach(card => {
        card.hidden = entries.length > 0;
      });

      this.querySelectorAll('[data-source-type="local-filter"]').forEach(button => {
        const key = button.dataset.localFilterKey;
        const value = button.dataset.localFilterValue;
        const count = cards.filter(card => card.getAttribute(`data-local-${key}`)?.split('|').includes(value)).length;
        const output = button.querySelector('[data-local-filter-count]');
        if (output) output.textContent = String(count);
      });

      if (entries.length > 0) {
        const label = visibleCount === 1 ? this.dataset.localCountOne : this.dataset.localCountOther?.replace('[count]', String(visibleCount));
        this.querySelectorAll('[data-facets-result-count]').forEach(output => {
          output.textContent = label || String(visibleCount);
        });
      } else {
        this.querySelectorAll('[data-facets-result-count]').forEach(output => {
          output.textContent = output.dataset.defaultCount || output.textContent;
        });
      }
    }

    initializeStickyToolbar() {
      this.stickyBar = this.querySelector('[data-facets-sticky-bar]');
      this.stickyAnchor = this.querySelector('[data-facets-sticky-anchor]');
      if (!this.stickyBar || !this.stickyAnchor || this.stickyBar.dataset.stickyEnabled === 'false') return;

      const { signal } = this.abortController;
      const scheduleUpdate = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = null;
          this.updateStickyToolbar();
        });
      };

      window.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
      window.addEventListener('resize', scheduleUpdate, { passive: true, signal });
      document.addEventListener('shopify:section:load', scheduleUpdate, { signal });
      scheduleUpdate();
    }

    updateStickyToolbar() {
      if (!this.stickyBar || !this.stickyAnchor) return;

      const headerOffset = Math.max(0, Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--site-header-offset')
      ) || 0);
      const anchorBounds = this.stickyAnchor.getBoundingClientRect();
      const resultsBounds = this.resultsSection?.getBoundingClientRect();
      const barHeight = this.stickyBar.offsetHeight;
      const shouldStick = anchorBounds.top <= headerOffset
        && (!resultsBounds || resultsBounds.bottom > headerOffset + barHeight);

      this.stickyBar.classList.toggle('is-sticky', shouldStick);
      this.stickyAnchor.style.height = shouldStick ? `${barHeight}px` : '';

      if (shouldStick) {
        this.stickyBar.style.setProperty('--facets-sticky-top', `${headerOffset}px`);
      } else {
        this.stickyBar.style.removeProperty('--facets-sticky-top');
      }
    }

    onExternalClick(event) {
      if (this.contains(event.target)) return;

      const link = event.target.closest('[data-collection-results-section] [data-facets-link], [data-collection-results-section] .collection-main__pagination a');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      this.render(link.href);
    }

    onClick(event) {
      const openButton = event.target.closest('[data-facets-open]');
      if (openButton) {
        this.openDrawer({ target: openButton.dataset.facetTarget });
        return;
      }

      if (event.target.closest('[data-facets-close]')) {
        this.closeDrawer();
        return;
      }

      if (event.target === this.drawer) {
        this.closeDrawer();
        return;
      }

      const visualFilter = event.target.closest('[data-visual-filter]');
      if (visualFilter && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        if (visualFilter.dataset.sourceType === 'local-filter') {
          this.toggleLocalFilter(visualFilter);
          return;
        }
        this.render(this.visualFilterUrl(visualFilter));
        return;
      }

      const link = event.target.closest('[data-facets-link], .collection-main__pagination a');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      const keepDrawerOpen = Boolean(link.closest('[data-facets-drawer]'));
      this.render(link.href, { keepDrawerOpen });
    }

    onChange(event) {
      if (event.target.matches('[data-facet-search]')) return;

      const form = event.target.closest('[data-facets-form]');
      if (!form) return;

      const priceRange = event.target.closest('[data-price-range]');
      if (priceRange) {
        this.syncPriceRange(priceRange, event.target);
        this.scheduleRender(form, { keepDrawerOpen: true, delay: 0 });
        return;
      }

      if (event.target.matches('input, select')) {
        this.render(this.formUrl(form), { keepDrawerOpen: true });
      }
    }

    onInput(event) {
      const search = event.target.closest('[data-facet-search]');
      if (search) {
        this.filterFacetValues(search);
        return;
      }

      const priceRange = event.target.closest('[data-price-range]');
      const form = event.target.closest('[data-facets-form]');
      if (!priceRange || !form) return;

      this.syncPriceRange(priceRange, event.target);
      if (event.target.matches('[data-price-number-min], [data-price-number-max]')) {
        this.scheduleRender(form, { keepDrawerOpen: true, delay: 450 });
      }
    }

    filterFacetValues(search) {

      const container = search.closest('.facets-drawer__group-content');
      const items = [...container.querySelectorAll('[data-facet-value]')];
      const query = this.normalize(search.value);
      let visibleCount = 0;

      items.forEach(item => {
        const label = this.normalize(item.querySelector('[data-facet-label]')?.textContent || '');
        const visible = label.includes(query);
        item.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      const empty = container.querySelector('[data-facet-search-empty]');
      if (empty) empty.hidden = visibleCount > 0;
    }

    onSubmit(event) {
      const form = event.target.closest('[data-facets-form], [data-facets-sort-form]');
      if (!form) return;
      event.preventDefault();
      this.render(this.formUrl(form), { keepDrawerOpen: Boolean(form.closest('[data-facets-drawer]')) });
    }

    scheduleRender(form, options = {}) {
      window.clearTimeout(this.priceRenderTimer);
      const delay = options.delay ?? 0;
      this.priceRenderTimer = window.setTimeout(() => {
        this.render(this.formUrl(form), { keepDrawerOpen: options.keepDrawerOpen });
      }, delay);
    }

    initializePriceRanges() {
      this.querySelectorAll('[data-price-range]').forEach(container => this.syncPriceRange(container));
    }

    syncPriceRange(container, source) {
      const rangeMax = Number(container.dataset.rangeMax) || 0;
      const minSlider = container.querySelector('[data-price-slider-min]');
      const maxSlider = container.querySelector('[data-price-slider-max]');
      const minNumber = container.querySelector('[data-price-number-min]');
      const maxNumber = container.querySelector('[data-price-number-max]');
      if (!minSlider || !maxSlider || !minNumber || !maxNumber || rangeMax <= 0) return;

      const sourceIsNumber = source === minNumber || source === maxNumber;
      let minimum = sourceIsNumber ? this.priceNumber(minNumber.value, 0) : Number(minSlider.value);
      let maximum = sourceIsNumber ? this.priceNumber(maxNumber.value, rangeMax) : Number(maxSlider.value);

      minimum = Math.min(Math.max(minimum, 0), rangeMax);
      maximum = Math.min(Math.max(maximum, 0), rangeMax);
      if (source === minSlider || source === minNumber) minimum = Math.min(minimum, maximum);
      if (source === maxSlider || source === maxNumber) maximum = Math.max(maximum, minimum);

      minSlider.value = String(minimum);
      maxSlider.value = String(maximum);
      minNumber.value = minimum <= 0 ? '' : String(minimum);
      maxNumber.value = maximum >= rangeMax ? '' : String(maximum);

      container.style.setProperty('--price-start', `${(minimum / rangeMax) * 100}%`);
      container.style.setProperty('--price-end', `${(maximum / rangeMax) * 100}%`);
      const formatter = this.priceFormatter(container.dataset.locale, container.dataset.currency, container.dataset.hideTrailingZeros === 'true');
      const minOutput = container.querySelector('[data-price-min-output]');
      const maxOutput = container.querySelector('[data-price-max-output]');
      if (minOutput) minOutput.textContent = formatter.format(minimum);
      if (maxOutput) maxOutput.textContent = formatter.format(maximum);
    }

    priceNumber(value, fallback) {
      if (String(value).trim() === '') return fallback;
      const number = Number(value);
      return Number.isFinite(number) ? number : fallback;
    }

    priceFormatter(locale, currency, hideTrailingZeros = false) {
      try {
        const options = {
          style: 'currency',
          currency: currency || 'EUR',
          maximumFractionDigits: 2
        };
        if (hideTrailingZeros) options.minimumFractionDigits = 0;
        return new Intl.NumberFormat(locale || document.documentElement.lang, options);
      } catch (_error) {
        return { format: value => String(value) };
      }
    }

    formUrl(form) {
      const url = new URL(form.action, location.origin);
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      new URL(location.href).searchParams.getAll(this.localFilterParameter || 'local_filter').forEach(value => {
        params.append(this.localFilterParameter || 'local_filter', value);
      });
      url.search = params.toString();
      return url.href;
    }

    visualFilterUrl(item, options = {}) {
      const sourceType = item.dataset.sourceType;
      if (sourceType === 'custom' && !options.forceRemove) return item.href;

      const url = new URL(location.href);
      const kind = item.dataset.visualKind;
      const isActive = item.dataset.active === 'true';
      url.searchParams.delete('page');

      if (kind === 'category') {
        this.removeVisualValues(url, 'category');
        const navigation = item.closest('[data-visual-navigation]');
        const isDefault = item.dataset.defaultCategory === 'true';
        const targetPath = item.dataset.targetPath;

        if (isDefault || options.forceRemove) {
          url.pathname = navigation?.dataset.basePath || targetPath || url.pathname;
          return url.href;
        }

        if (sourceType === 'collection' && targetPath) {
          url.pathname = targetPath;
        } else if (navigation?.dataset.basePath) {
          url.pathname = navigation.dataset.basePath;
        }
        this.appendVisualValue(url, item);
        return url.href;
      }

      if (isActive || options.forceRemove) {
        this.removeVisualValue(url, item.dataset.filterParam, item.dataset.filterValue);
        return url.href;
      }

      const navigation = item.closest('[data-visual-navigation]');
      if (navigation?.dataset.selectionMode === 'single') this.removeVisualValues(url, 'need');
      this.appendVisualValue(url, item);
      return url.href;
    }

    removeVisualValues(url, kind) {
      this.querySelectorAll(`[data-visual-filter][data-visual-kind="${kind}"]`).forEach(item => {
        this.removeVisualValue(url, item.dataset.filterParam, item.dataset.filterValue);
      });
    }

    removeVisualValue(url, parameter, value) {
      if (!parameter || !value) return;
      const retained = url.searchParams.getAll(parameter).filter(current => current !== value);
      url.searchParams.delete(parameter);
      retained.forEach(current => url.searchParams.append(parameter, current));
    }

    appendVisualValue(url, item) {
      const parameter = item.dataset.filterParam;
      const value = item.dataset.filterValue;
      if (!parameter || !value) return;
      if (!url.searchParams.getAll(parameter).includes(value)) url.searchParams.append(parameter, value);
    }

    async render(url, options = {}) {
      const resultsSection = document.querySelector('[data-collection-results-section]');
      const resultsSectionId = resultsSection?.dataset.collectionResultsSection;
      if (!this.sectionId || !resultsSectionId) {
        location.assign(url);
        return;
      }

      const currentUrl = new URL(url, location.origin);
      const requestUrl = new URL(currentUrl.href);
      const bannerSection = document.querySelector('[data-collection-banner-section]');
      const bannerSectionId = bannerSection?.dataset.collectionBannerSection;
      const shouldUpdateBanner = Boolean(bannerSectionId && currentUrl.pathname !== this.dataset.collectionPath);
      const sectionIds = [...new Set([this.sectionId, resultsSectionId])];
      if (shouldUpdateBanner) sectionIds.push(bannerSectionId);
      requestUrl.searchParams.delete('section_id');
      requestUrl.searchParams.set('sections', sectionIds.join(','));
      const drawerScroll = this.querySelector('.facets-drawer__body')?.scrollTop || 0;
      const categoryScroll = this.querySelector('.collection-visual-nav__categories')?.scrollLeft || 0;
      const needsScroll = this.querySelector('.collection-visual-nav__needs')?.scrollLeft || 0;
      const activeElementId = document.activeElement?.id;

      this.fetchController?.abort();
      this.fetchController = new AbortController();
      this.classList.add('facets-is-loading');
      resultsSection.classList.add('facets-is-loading');
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(requestUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.fetchController.signal
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const renderedSections = await response.json();
        const facetsHtml = renderedSections[this.sectionId];
        const resultsHtml = renderedSections[resultsSectionId];
        if (!facetsHtml || !resultsHtml) throw new Error('Updated collection sections were not found.');

        const facetsDocument = new DOMParser().parseFromString(facetsHtml, 'text/html');
        const resultsDocument = new DOMParser().parseFromString(resultsHtml, 'text/html');
        const next = facetsDocument.querySelector(`storefront-facets[data-section-id="${CSS.escape(this.sectionId)}"]`);
        const nextResults = resultsDocument.querySelector(`[data-collection-results-section="${CSS.escape(resultsSectionId)}"]`);
        if (!next || !nextResults) throw new Error('Updated collection markup was not found.');

        let nextBanner = null;
        if (shouldUpdateBanner) {
          const bannerHtml = renderedSections[bannerSectionId];
          if (!bannerHtml) throw new Error('Updated collection banner was not found.');
          const bannerDocument = new DOMParser().parseFromString(bannerHtml, 'text/html');
          nextBanner = bannerDocument.querySelector(`[data-collection-banner-section="${CSS.escape(bannerSectionId)}"]`);
          if (!nextBanner) throw new Error('Updated collection banner markup was not found.');
        }

        if (options.updateHistory !== false && currentUrl.href !== location.href) {
          history.pushState({}, '', currentUrl);
        }

        if (nextBanner) bannerSection.replaceWith(nextBanner);
        resultsSection.replaceWith(nextResults);

        if (options.keepDrawerOpen) {
          const currentChrome = this.querySelector('.facets__chrome');
          const nextChrome = next.querySelector('.facets__chrome');
          const currentForm = this.querySelector('[data-facets-drawer-form]');
          const nextForm = next.querySelector('[data-facets-drawer-form]');

          if (!currentChrome || !nextChrome || !currentForm || !nextForm) {
            throw new Error('Updated filter drawer markup was not found.');
          }

          currentChrome.replaceWith(nextChrome);
          currentForm.replaceWith(nextForm);
          this.dataset.collectionPath = next.dataset.collectionPath;
          this.resultsSection = nextResults;
          this.stickyBar = this.querySelector('[data-facets-sticky-bar]');
          this.stickyAnchor = this.querySelector('[data-facets-sticky-anchor]');
          this.initializePriceRanges();
          this.syncLocalFilterButtons();
          this.applyLocalFilters();
          this.updateStickyToolbar();
          this.classList.remove('facets-is-loading');
          this.removeAttribute('aria-busy');

          const nextDrawerBody = this.querySelector('.facets-drawer__body');
          if (nextDrawerBody) nextDrawerBody.scrollTop = drawerScroll;
          if (activeElementId) {
            this.querySelector(`#${CSS.escape(activeElementId)}`)?.focus({ preventScroll: true });
          }
        } else {
          this.replaceWith(next);
          requestAnimationFrame(() => {
            const nextCategories = next.querySelector('.collection-visual-nav__categories');
            const nextNeeds = next.querySelector('.collection-visual-nav__needs');
            if (nextCategories) nextCategories.scrollLeft = categoryScroll;
            if (nextNeeds) nextNeeds.scrollLeft = needsScroll;
            if (activeElementId) next.querySelector(`#${CSS.escape(activeElementId)}`)?.focus({ preventScroll: true });
          });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        location.assign(currentUrl.href);
      }
    }

    openDrawer(options = {}) {
      if (!this.drawer) return;
      if (!this.drawer.open) this.drawer.showModal();
      document.documentElement.classList.add('facets-drawer-open');
      const reveal = () => {
        this.drawer.classList.add('is-visible');
        const body = this.drawer.querySelector('.facets-drawer__body');
        const targetGroup = options.target
          ? [...this.drawer.querySelectorAll('[data-facet-group]')].find(group => group.dataset.facetGroup === options.target)
          : null;
        if (targetGroup) {
          targetGroup.open = true;
          requestAnimationFrame(() => targetGroup.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }));
        } else if (body && options.scrollTop != null) body.scrollTop = options.scrollTop;
        const focusTarget = options.focusId ? this.drawer.querySelector(`#${CSS.escape(options.focusId)}`) : targetGroup?.querySelector('summary');
        (focusTarget || this.drawer.querySelector('[data-facets-close]'))?.focus({ preventScroll: true });
      };
      if (options.instant) reveal();
      else requestAnimationFrame(reveal);
    }

    closeDrawer() {
      if (!this.drawer?.open) return;
      this.drawer.classList.remove('is-visible');

      const finish = () => {
        if (this.drawer?.open) this.drawer.close();
        this.unlockPage();
      };

      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !this.drawerPanel) {
        finish();
        return;
      }

      this.drawerPanel.addEventListener('transitionend', finish, { once: true });
    }

    unlockPage() {
      document.documentElement.classList.remove('facets-drawer-open');
    }

    normalize(value) {
      return value.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.fetchController?.abort();
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      window.clearTimeout(this.priceRenderTimer);
      this.unlockPage();
    }
  });
}
